import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useApiClient } from "../../../api";
import { FruitifyLoading } from "../../../shared-components/Loading";
import { AssessmentObjectiveAccordion } from "./AssessmentObjectiveAccordion";
import { Add, ExpandLess, ExpandMore, Info } from "@mui/icons-material";
import { AOSelectorDialog } from "./AOSelectorDialog";
import { MESSAGE_CATEGORIES } from "../../../shared-components/LoadingMessages";
import { TagAutocomplete } from "../../../shared-components/TagAutoComplete";
import ExpandableTagsBox from "../../../shared-components/ExpandableTagsBox";
import MarkSchemeSearch from "./MarkSchemeSearch";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";

const MostUsedSuggestionDropdown = ({ onSelect, question }) => {
  const [suggestions, setSuggestions] = useState({
    personal: [],
    trending: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const ApiClient = useApiClient();
  const fetchMostUsedCombinations = async () => {
    const educationLevelId =
      question?.educationLevel?.id || question?.yearGroup?.educationLevel?.id;
    if (!question?.subject?.id || !educationLevelId) {
      setError(true);
      setLoading(false);
      return;
    }
    try {
      setError(false);
      const response =
        await ApiClient.question.getMostUsedAssessmentObjectiveCombinations({
          subject: question?.subject.id,
          educationLevel: educationLevelId,
        });
      if (response) {
        setSuggestions(response.mostUsedAssessmentObjectives);
      } else {
        console.error("Error fetching most used combinations");
      }
    } catch (error) {
      setError(true);
      console.error("Error fetching most used combinations", error);
    } finally {
      setLoading(false);
    }
  };

  const combinationOptions = [
    ...(suggestions.personal || []).map((suggestion, index) => ({
      key: `personal-${index}`,
      group: "personal",
      suggestion,
    })),
    ...(suggestions.trending || []).map((suggestion, index) => ({
      key: `trending-${index}`,
      group: "trending",
      suggestion,
    })),
  ];

  const handleSelectCombination = (event) => {
    const selectedOption = combinationOptions.find(
      (option) => option.key === event.target.value,
    );
    if (!selectedOption) return;

    const newMapping = {};
    selectedOption.suggestion.objectives.forEach((item) => {
      newMapping[item.assessmentObjective.id] = item.marks;
    });
    onSelect(newMapping);
  };

  useEffect(() => {
    fetchMostUsedCombinations(); // TODO: Prevent refetch?
  }, []);

  return (
    <FormControl size="small" sx={{ mb: 3, width: "50%" }}>
      <InputLabel id="combo-select-label">
        Quick-fill from common combinations
      </InputLabel>
      <Select
        labelId="combo-select-label"
        label="Quick-fill from common combinations"
        value="" // Keep empty so it acts like a trigger/action
        onChange={handleSelectCombination}
      >
        <MenuItem value="" disabled>
          <em>
            {loading
              ? "Loading combinations…"
              : error
                ? "Unable to load combinations"
                : combinationOptions.length === 0
                  ? "No common combinations yet"
                  : "Choose a combination"}
          </em>
        </MenuItem>

        {suggestions.personal?.length > 0 && [
          <ListSubheader key="personal-header">
            Your Recent Combinations
          </ListSubheader>,
          ...suggestions.personal.map((s, idx) => (
            <MenuItem key={`personal-${idx}`} value={`personal-${idx}`}>
              {s.objectives
                .map((o) => `${o.assessmentObjective.identifier} (${o.marks})`)
                .join(" + ")}
            </MenuItem>
          )),
        ]}

        <Divider />

        {suggestions.trending?.length > 0 && [
          <ListSubheader key="trending-header">
            Trending (All Users)
          </ListSubheader>,
          ...suggestions.trending.map((s, idx) => (
            <MenuItem key={`trending-${idx}`} value={`trending-${idx}`}>
              {s.objectives
                .map((o) => `${o.assessmentObjective.identifier} (${o.marks})`)
                .join(" + ")}
            </MenuItem>
          )),
        ]}
      </Select>
    </FormControl>
  );
};

const MarkSchemeQuickCreate = ({
  question,
  sortedAvailableObjectives,
  setModelAnswerGuidance,
  setObjectives,
  setSuggesting,
  collapsible = true,
  hideSmartSuggest = false,
  title = "Quick create",
  onAOChange = () => {},
}) => {
  const [chosenAos, setChosenAos] = useState({});
  const [open, setOpen] = useState(false);
  const [suggestComplete, setSuggestComplete] = useState(false);
  const ApiClient = useApiClient();

  const handleUpdateMark = (id, marks) => {
    setChosenAos((prev) => ({
      ...prev,
      [id]: marks,
    }));
  };

  const handleRemoveAo = (id) => {
    setChosenAos((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  useEffect(() => {
    onAOChange(chosenAos);
  }, [chosenAos]);

  const handleSmartSuggest = async () => {
    setSuggesting(true);

    try {
      const response = await ApiClient.question.suggestMarkScheme({
        question: question.id,
        aoConfig: JSON.stringify(chosenAos),
      });

      if (response && response.success) {
        const updatedObjectives = sortedAvailableObjectives
          .filter((obj) =>
            response.suggestedAos.some((ao) => ao.identifier === obj.id),
          )
          .map((obj) => ({
            id: obj.id,
            identifier: obj.identifier,
            assessmentObjective: obj,
            levels:
              response.suggestedAos.find((ao) => ao.identifier === obj.id)
                ?.levels || [],
          }));

        setModelAnswerGuidance(response.modelAnswerGuidance);
        setObjectives(updatedObjectives);
        setSuggestComplete(true);
      } else {
        console.error("Error suggesting mark scheme");
      }
    } catch (error) {
      console.error("Error suggesting mark scheme:", error);
    } finally {
      setSuggesting(false);
    }
  };
  const readyToSuggest = Object.keys(chosenAos).length > 0;

  return (
    <Box sx={{ mb: 1 }}>
      {collapsible && (
        <Button
          variant="text"
          size="small"
          onClick={() => setSuggestComplete(!suggestComplete)}
          startIcon={!suggestComplete ? <ExpandLess /> : <ExpandMore />}
          sx={{ mb: 1, textTransform: "none", fontWeight: 600 }}
        >
          {!suggestComplete ? "Hide" : "Use quick create"}
        </Button>
      )}

      <Collapse in={!suggestComplete}>
        <AOSelectorDialog
          open={open}
          setOpen={setOpen}
          availableObjectives={sortedAvailableObjectives.filter(
            (objective) =>
              !Object.prototype.hasOwnProperty.call(chosenAos, objective.id),
          )}
          onSelect={(ao) => {
            if (!chosenAos[ao.id]) {
              handleUpdateMark(ao.id, 1);
            }
            setOpen(false); // optionally close dialog after adding
          }}
        />
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start" // Keeps the dropdown vertically centered with the title
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          {/* Left Side: Grouped Title and Info */}
          <Box>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Info fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Tell us the Assessment Objectives and let us handle the rest.
              </Typography>
            </Stack>
          </Box>

          {/* Right Side: Dropdown */}
          <MostUsedSuggestionDropdown
            question={question}
            onSelect={setChosenAos}
          />
        </Stack>
        <Stack spacing={2} sx={{ mb: 1 }}>
          {/* Selected AOs List */}
          <Stack spacing={1}>
            {Object.entries(chosenAos).map(([aoId, marks]) => {
              const aoDetails = sortedAvailableObjectives.find(
                (o) => o.id === aoId,
              );
              return (
                <Stack
                  key={aoId}
                  direction="row"
                  spacing={2} // Increased spacing for better separation
                  alignItems="center"
                  sx={{
                    p: 1.5,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {/* Text Section: FlexGrow allows it to fill space */}
                  <Stack direction="column" sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {aoDetails?.description?.length > 100
                        ? `${aoDetails.description.substring(0, 100)}...`
                        : aoDetails?.description || "Unknown Objective"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {aoDetails?.focusArea?.length > 100
                        ? `${aoDetails.focusArea.substring(0, 100)}...`
                        : aoDetails?.focusArea || ""}
                    </Typography>
                  </Stack>

                  {/* Actions Section: Fixed width prevents squashing */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      flexShrink: 0,
                      width: "180px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <TextField
                      label="Marks"
                      type="number"
                      size="small"
                      value={marks}
                      onChange={(e) => handleUpdateMark(aoId, e.target.value)}
                      sx={{ width: "70px" }}
                      inputProps={{ min: 0 }}
                    />
                    <Button
                      variant="card-action"
                      onClick={() => handleRemoveAo(aoId)}
                      sx={{ minWidth: "85px", height: "40px" }}
                    >
                      Remove
                    </Button>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Button
            data-testid={"add-additional-ao-button"}
            startIcon={<Add />}
            variant={"card-action"}
            onClick={() => setOpen(true)}
          >
            Add
          </Button>
          {!hideSmartSuggest && (
            <FormControl sx={{ alignItems: "flex-end" }}>
              <Button
                sx={{ maxWidth: 200 }}
                variant={"outlined"}
                endIcon={<FruitifyIcon name={"UseAI"} />}
                onClick={handleSmartSuggest}
                disabled={!readyToSuggest}
              >
                Smart suggest
              </Button>
            </FormControl>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

const MarkSchemeAssessmentObjectives = ({
  chosenObjectives,
  onObjectivesChange,
  availableObjectives,
  readOnly = false,
}) => {
  const [open, setOpen] = useState(false);
  const handleObjectiveChange = (updatedObjective) => {
    const newObjectives = chosenObjectives
      .map((obj) =>
        obj.assessmentObjective.id === updatedObjective.assessmentObjective.id
          ? updatedObjective
          : obj,
      )
      .sort((a, b) =>
        a.assessmentObjective.identifier.localeCompare(
          b.assessmentObjective.identifier,
        ),
      );

    onObjectivesChange(newObjectives);
  };

  const handleAddAo = (ao) => {
    if (
      chosenObjectives.some(
        (obj) => obj.assessmentObjective.identifier === ao.identifier,
      )
    )
      return;
    const newObjective = {
      assessmentObjective: ao,
      levels: [],
    };
    onObjectivesChange([...chosenObjectives, newObjective]);
  };

  const handleRemoveObjective = (ao) => {
    const newObjectives = chosenObjectives.filter(
      (obj) => obj.assessmentObjective.id !== ao.assessmentObjective.id,
    );
    onObjectivesChange(newObjectives);
  };

  const sortedAvailableObjectives = availableObjectives
    .filter(
      (obj) =>
        !chosenObjectives.some(
          (chosen) => chosen.assessmentObjective?.identifier === obj.identifier,
        ),
    )
    .sort((a, b) => a.identifier.localeCompare(b.identifier));
  return (
    <>
      <AOSelectorDialog
        open={open}
        setOpen={setOpen}
        availableObjectives={sortedAvailableObjectives}
        onSelect={(ao) => {
          handleAddAo(ao);
          setOpen(false); // optionally close dialog after adding
        }}
      />
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography variant={"subtitle1"}>Assessment Objectives</Typography>
        {!readOnly && (
          <Button
            startIcon={<Add />}
            variant={"card-action"}
            onClick={() => setOpen(true)}
          >
            Add
          </Button>
        )}
      </Stack>
      {chosenObjectives.map((objective) => (
        <Box sx={{ mb: 2 }} key={objective.assessmentObjective.id}>
          <AssessmentObjectiveAccordion
            objective={objective}
            onObjectiveChange={handleObjectiveChange}
            onObjectiveRemove={handleRemoveObjective}
            readOnly={readOnly}
          />
        </Box>
      ))}
    </>
  );
};

const MarkSchemeDetailsAccordion = ({
  markScheme,
  setMarkSchemeName,
  tags,
  setTags,
  question,
  readOnly = false,
}) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const theme = useTheme();

  // Don't render if markScheme is null
  if (!markScheme) {
    return null;
  }
  return (
    <Box sx={{ mb: 2 }}>
      <Accordion expanded={detailsExpanded} variant={"outlined"}>
        <AccordionSummary onClick={() => setDetailsExpanded(!detailsExpanded)}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            {/* Left-aligned text content */}
            <Stack direction="column" spacing={0.5} sx={{ ml: 1 }}>
              <Typography
                variant="subtitle2"
                fontWeight="medium"
                sx={{ color: theme.palette.primary.main }}
              >
                Mark scheme details
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ color: theme.palette.primary.main }}
              >
                Information used for quickly looking up mark schemes for re-use
              </Typography>
            </Stack>

            {/* Right-aligned expand icon */}
            <ExpandMore sx={{ color: theme.palette.primary.main }} />
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          {readOnly ? (
            <Typography>
              <strong>Name: </strong>
              {markScheme.name ? markScheme.name : "Not provided"}
            </Typography>
          ) : (
            <TextField
              disabled={readOnly}
              label={"Name (Optional)"}
              value={markScheme.name}
              onChange={(e) => setMarkSchemeName(e.target.value)}
              helperText={
                !readOnly &&
                "Add a name to easily find and re-use the mark scheme (optional)"
              }
              style={{
                minWidth: "100%",
                borderRadius: "4px",
              }}
              sx={{ mb: 2 }}
            />
          )}
          {readOnly ? (
            <ExpandableTagsBox tags={markScheme.tags} />
          ) : (
            <TagAutocomplete
              value={tags}
              onChange={setTags}
              educationLevel={question.educationLevel}
              subject={question.subject}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const LongTextMarkSchemeStep = ({
  question,
  markScheme,
  onChangeMarkScheme,
  readOnly = false,
}) => {
  if (!readOnly && typeof onChangeMarkScheme !== "function") {
    throw new Error(
      "MarkSchemeStep: `onChangeMarkScheme` must be provided when `readOnly` is false.",
    );
  }
  const ApiClient = useApiClient();
  const [totalMarks, setTotalMarks] = useState(0);
  const [suggesting, setSuggesting] = useState(false);
  const [tags, setTags] = useState([]);
  const [markSchemeName, setMarkSchemeName] = useState("");
  const [availableAssessmentObjectives, setAvailableAssessmentObjectives] =
    useState([]);
  const [modelAnswerGuidance, setModelAnswerGuidance] = useState(
    markScheme?.modelAnswerGuidance || "",
  );
  const [objectives, setObjectives] = useState(markScheme?.objectives || []);
  const [initialized, setInitialized] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Only initialize once from question on mount, not on every question change
  useEffect(() => {
    if (question?.markScheme && !initialized) {
      setModelAnswerGuidance(question.markScheme.modelAnswerGuidance);
      setObjectives(question.markScheme.objectives);
      setTags(question.markScheme.tags?.map((t) => t.tag) || []);
      setMarkSchemeName(question.markScheme.name);
      setInitialized(true);
    }
  }, [question, initialized]);

  useEffect(() => {
    if (onChangeMarkScheme) {
      onChangeMarkScheme({
        modelAnswerGuidance,
        objectives,
        tags,
        markSchemeName,
      });
    }
  }, [modelAnswerGuidance, objectives, tags, markSchemeName]);

  const handleMarkSchemeSelected = (existingMarkScheme) => {
    setModelAnswerGuidance(existingMarkScheme.modelAnswerGuidance);
    setObjectives(existingMarkScheme.objectives);
    setTags(existingMarkScheme.tags.map((t) => t.tag));
    setMarkSchemeName(existingMarkScheme.name);
  };

  useEffect(() => {
    const newTotalMarks = markScheme?.objectives?.reduce((sum, obj) => {
      const maxMark = obj.levels?.length
        ? Math.max(...obj.levels.map((l) => l.maxMark || 0))
        : 0;
      return sum + maxMark;
    }, 0);

    setTotalMarks(newTotalMarks);
  }, [markScheme]);

  const fetchAssessmentObjectives = async () => {
    try {
      const response = await ApiClient.question.getAssessmentObjectives({
        educationLevel: question?.educationLevel.id,
        subject: question?.subject.id,
      });
      if (response) {
        setAvailableAssessmentObjectives(response.assessmentObjectives);
      }
    } catch (error) {
      console.error("Failed to fetch AOs", error);
    }
  };

  useEffect(() => {
    if (!readOnly) {
      fetchAssessmentObjectives();
    }
  }, []);

  const handleObjectivesChange = (objectives) => {
    setObjectives(objectives);
  };

  if (suggesting) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80%", // make it fill the available space
          width: "100%",
        }}
      >
        <FruitifyLoading category={MESSAGE_CATEGORIES.aiMagic} />
      </Box>
    );
  }

  return (
    <Stack
      direction={"column"}
      spacing={2}
      sx={{ mt: 2 }}
      data-testid={"mark-scheme-step"}
    >
      {!readOnly && (
        <Box sx={{ mb: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            startIcon={isSearchOpen ? <ExpandLess /> : <ExpandMore />}
            sx={{ mb: 1, textTransform: "none", fontWeight: 600 }}
          >
            {isSearchOpen ? "Hide" : "Use existing mark scheme (legacy)"}
          </Button>

          <Collapse in={isSearchOpen} sx={{ mb: 1 }}>
            <MarkSchemeSearch
              onSelect={handleMarkSchemeSelected}
              subject={question.subject}
              educationLevel={question.educationLevel}
            />
          </Collapse>
          <Divider />
          <MarkSchemeQuickCreate
            sortedAvailableObjectives={availableAssessmentObjectives}
            setObjectives={setObjectives}
            setModelAnswerGuidance={setModelAnswerGuidance}
            question={question}
            setSuggesting={setSuggesting}
          />
        </Box>
      )}
      <Divider />
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography variant={"h5"}>Final mark scheme</Typography>

        <FormControl sx={{ alignItems: "flex-end" }}>
          <TextField
            disabled
            label={"Total Marks"}
            value={totalMarks}
            size="small"
            type="number"
            sx={{ width: 120 }}
          />
        </FormControl>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Info fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">
          Optionally review, edit and refine the mark scheme before saving
        </Typography>
      </Stack>

      <MarkSchemeDetailsAccordion
        markScheme={markScheme}
        setMarkSchemeName={setMarkSchemeName}
        tags={tags}
        setTags={setTags}
        question={question}
        readOnly={readOnly}
      />
      <TextField
        multiline
        rows={3}
        label={"Model answer guidance"}
        value={modelAnswerGuidance}
        onChange={(e) => setModelAnswerGuidance(e.target.value)}
        placeholder="Add some additional model answer guidance for your mark scheme (smart suggest can polish it up for you)..."
        style={{
          minWidth: "100%",
          borderRadius: "4px",
        }}
      />
      <MarkSchemeAssessmentObjectives
        question={question}
        chosenObjectives={objectives}
        availableObjectives={availableAssessmentObjectives}
        onObjectivesChange={handleObjectivesChange}
        readOnly={readOnly}
      />
    </Stack>
  );
};

export {
  LongTextMarkSchemeStep,
  MostUsedSuggestionDropdown,
  MarkSchemeQuickCreate,
};
